/**
 * Light and shadow in the world, fitted to the figures as they are drawn now (1x pixel art, about
 * 89 px tall, slender): the time of day, the shadows under and behind everyone, and the night with
 * its pools of lamplight. The key light is up and to the right, as the figures are shaded, so cast
 * shadows fall toward the lower left. Everything is hard-edged or dithered pixel art, drawn once per
 * size and kept.
 *
 * All of it is skipped (not hidden) when the player turns off "แสงและเงา" (`settings.lighting`):
 * no shadow sprites, no dusk or night layer, plain daylight indoors and out.
 */
import { hash2, type Entity, type GameMap, type World } from '@nine/shared';
import { tierIndex } from '../graphics';
import { settings } from '../settings';
import type { IsoCamera } from './camera';
import { drawCastShadow, drawContactShadow, TINTS, type ShadowTint } from './contactShadow';
import { artX, artY, canvas, ctx2d, resOf } from './pixel';
import type { SpriteSet } from './sprites';
import { plazaLanternRing, type Prop } from './terrain';

/** Whether lights and shadows are drawn at all (the setting, read live every frame). */
export const lightingOn = (): boolean => settings.lighting;

/** How dark the world is at an hour (0 day – 1 deep night), and how warm the light is (dusk, dawn). */
export function nightAt(hour: number): { dark: number; warm: number } {
  const ramp = (a: number, b: number) => Math.min(1, Math.max(0, (hour - a) / (b - a)));
  if (hour >= 17 && hour < 21)
    return { dark: ramp(18.5, 21), warm: hour < 20 ? ramp(17, 18.5) : 1 - ramp(20, 21) };
  if (hour >= 4 && hour < 7.5)
    return { dark: 1 - ramp(4.5, 6.5), warm: hour < 6 ? ramp(4, 5.5) : 1 - ramp(6, 7.5) };
  return { dark: hour >= 21 || hour < 4.5 ? 1 : 0, warm: 0 };
}

/** The daylight a room gets (0 night – 1 noon): plain day when lighting is off. */
export const daylightAt = (hour: number): number => (lightingOn() ? 1 - nightAt(hour).dark : 1);

/** Sunrise and sunset hours: the sun's arc between them sets the cast shadows' length. */
const SUNRISE = 5;
const SUNSET = 19.5;

/** The sun's part in the shadows at an hour. */
interface Sun {
  /** Cast shadow length as a fraction of the figure's height (short at noon, long at dusk). */
  len: number;
  /** Cast shadow opacity: gone once the sun is down. */
  cast: number;
  /** Contact shadow opacity. */
  contact: number;
  tint: ShadowTint;
}

function sunAt(hour: number): Sun {
  const { dark, warm } = nightAt(hour);
  const t = (hour - SUNRISE) / (SUNSET - SUNRISE);
  const el = t > 0 && t < 1 ? Math.sin(Math.PI * t) : 0;
  const low = 1 - el;
  return {
    len: 0.3 + 0.7 * low * low,
    // Faint as the sun nears the horizon and its light thins (and none at night).
    cast: 0.4 * (1 - dark) * Math.min(1, el / 0.18),
    // Ambient occlusion stays at night, a little lighter than in the sun.
    contact: 0.55 - 0.15 * dark,
    tint: warm > 0.5 || el < 0.25 ? 2 : warm > 0.1 || el < 0.5 ? 1 : 0,
  };
}

/**
 * A figure's footprint in art pixels from its anchor: half the width of its base (the stance, or a
 * robe's hem when wider), half its hips, and the middle of its stance.
 */
interface Footprint {
  base: number;
  body: number;
  cx: number;
}
const FOOT = new WeakMap<SpriteSet, Footprint | number>();
/** Frames looked at again after this many draws when a frame was still blank (not yet prepared). */
const RETRY = 90;

/** The footprint to use until a frame can be measured. */
const guess = (set: SpriteSet): Footprint =>
  set.h >= 50 ? { base: 9, body: 10, cx: 0 } : { base: set.h * 0.3, body: set.h * 0.4, cx: 0 };

/**
 * Measures a figure's standing frame: the feet (the lowest opaque row), its widest span over the
 * bottom tenth of its height (the stance, or a robe's hem over the feet, which a contact shadow
 * must show past) and the middle of its height (the hips, over a few rows so a weapon at the side
 * counts for little). Null while the frame is still blank.
 */
function measure(set: SpriteSet): Footprint | null {
  const img = set.idle[0];
  if (!img || !img.width || !img.height) return null;
  let data: Uint8ClampedArray;
  try {
    data = ctx2d(img).getImageData(0, 0, img.width, img.height).data;
  } catch {
    return null;
  }
  const r = resOf(img);
  const X = artX(img);
  const Y = artY(img);
  const W = img.width;
  const span = (artRow: number): [number, number] | null => {
    const y = Math.round((artRow - Y) * r);
    if (y < 0 || y >= img.height) return null;
    let a = -1;
    let b = -1;
    for (let x = 0; x < W; x++)
      if (data[(y * W + x) * 4 + 3]! > 128) {
        if (a < 0) a = x;
        b = x;
      }
    return a < 0 ? null : [a / r + X - set.ax, (b + 1) / r + X - set.ax];
  };
  let feet: [number, number] | null = null;
  for (let k = 1; k <= 6 && !feet; k++) feet = span(set.ay - k);
  if (!feet) return null;
  const cx = (feet[0] + feet[1]) / 2;
  let base = (feet[1] - feet[0]) / 2;
  for (let k = 1; k <= Math.max(3, set.h * 0.1); k++) {
    const s = span(set.ay - k);
    if (s) base = Math.max(base, cx - s[0], s[1] - cx);
  }
  const halves: number[] = [];
  for (let k = 0.35; k <= 0.6; k += 0.05) {
    const s = span(set.ay - set.h * k);
    if (s) halves.push((s[1] - s[0]) / 2);
  }
  halves.sort((a, b) => a - b);
  return { base, body: halves[halves.length >> 1] ?? base, cx };
}

function footprintOf(set: SpriteSet): Footprint {
  const got = FOOT.get(set);
  if (typeof got === 'object') return got;
  if (typeof got === 'number' && got > 0) {
    FOOT.set(set, got - 1);
    return guess(set);
  }
  const fp = measure(set);
  FOOT.set(set, fp ?? RETRY);
  return fp ?? guess(set);
}

/**
 * Half-width of a figure's contact shadow in screen pixels at draw scale `s`: a few pixels past its
 * base each side, so it shows round the feet and hem rather than hiding behind them.
 */
export function contactRadius(set: SpriteSet, s: number): number {
  const fp = footprintOf(set);
  return Math.max(3, Math.max(fp.base, fp.body * 0.8) + 2.5) * s;
}

/**
 * The shadows of a figure standing at (`x`, `ground`): the cast shadow toward the lower left for
 * the hour, then the contact shadow over it (the two together darkest at the feet). `lift` (0–0.5)
 * shrinks and fades them as the figure leaves the ground; `air` (pixels up) slides the cast shadow
 * away from the feet, as a jump's shadow does. Indoors (`hour` null) there is no sun: the contact
 * shadow alone.
 */
export function drawFigureShadows(
  g: CanvasRenderingContext2D,
  set: SpriteSet,
  facing: number,
  x: number,
  ground: number,
  s: number,
  hour: number | null,
  lift: number,
  air: number,
  alpha: number,
  tilt: number,
): void {
  const fp = footprintOf(set);
  const sun = sunAt(hour ?? 12);
  const cx = x + Math.round(fp.cx * s * (facing < 0 ? -1 : 1));
  const keep = 1 - lift;
  if (hour !== null && sun.cast > 0.01) {
    const len = sun.len * set.h * s;
    const along = air * sun.len;
    drawCastShadow(
      g,
      cx + Math.round((-2 / Math.sqrt(5)) * along),
      ground + Math.round((1 / Math.sqrt(5)) * along),
      len * keep,
      Math.max(2, fp.body * 0.8 * s * keep),
      alpha * sun.cast * keep,
      tilt,
      sun.tint,
    );
  }
  // Its middle a pixel in front of the feet, so more of it shows below the figure than behind. Off
  // the ground it narrows but stays dark: in a jump it is what shows how high the figure is.
  const rx = contactRadius(set, s) * (1 - 0.6 * lift);
  const a = alpha * sun.contact * (1 - 0.4 * lift);
  drawContactShadow(g, cx, ground + 1, rx, Math.max(1.5, rx * 0.42), a, tilt, sun.tint);
}

/**
 * Where a lantern carried by `e` hangs, as pixels up from its feet: from the hand at the side
 * (just under half the figure's height), or set down on the ground beside someone seated.
 */
export function lanternLift(e: Entity, height: number): number {
  return e.folk?.act === 'sit' ? 9 : Math.round(height * 0.4);
}

// ───────────────────────── scenery ─────────────────────────

/** Screen direction of every sun shadow along the iso ground (2:1), toward the lower left. */
const DX = -2 / Math.sqrt(5);
const DY = 1 / Math.sqrt(5);
/** Screen pixels a tile spans along the ground's +y axis (16 across and 8 down). */
const TILE_Y = Math.hypot(16, 8);

const BLOCKS = new Map<number, { img: HTMLCanvasElement; ox: number; oy: number }>();
/**
 * The ground shadow a building `w` tiles wide throws `len` tiles toward the lower left (+y on the
 * map): the strip of ground in front of its front-left wall, solid near the wall and dithered out
 * over its far third, in whole pixels. Drawn once per size and tint.
 */
function blockSprite(
  w: number,
  len: number,
  tint: ShadowTint,
): { img: HTMLCanvasElement; ox: number; oy: number } {
  const key = (w * 1024 + Math.round(len * 8)) * 4 + tint;
  let s = BLOCKS.get(key);
  if (s) return s;
  // Screen corners of the map rectangle [0, w] × [0, len], from its west corner.
  const xs = [0, w * 16, -len * 16, (w - len) * 16];
  const ys = [0, w * 8, len * 8, (w + len) * 8];
  const x0 = Math.floor(Math.min(...xs));
  const y0 = Math.floor(Math.min(...ys));
  const W = Math.ceil(Math.max(...xs)) - x0 + 1;
  const H = Math.ceil(Math.max(...ys)) - y0 + 1;
  const c = canvas(W, H);
  const g = ctx2d(c);
  g.fillStyle = TINTS[tint];
  for (let y = 0; y < H; y++)
    for (let x = 0; x < W; x++) {
      const u = (x + 0.5 + x0) / 16;
      const v = (y + 0.5 + y0) / 8;
      // Back to the map: `a` along the wall, `b` out from it.
      const a = (u + v) / 2;
      const b = (v - u) / 2;
      if (a < 0 || a > w || b < 0 || b > len) continue;
      const t = b / len;
      if (t > 0.6 && bayer(x, y) < ((t - 0.6) / 0.4) * 0.85) continue;
      g.fillRect(x, y, 1, 1);
    }
  s = { img: c, ox: -x0, oy: -y0 };
  BLOCKS.set(key, s);
  return s;
}

/** The props that throw a sun shadow, found once per set of props. */
const CASTERS = new WeakMap<readonly Prop[], Prop[]>();

/**
 * The sun shadows of buildings and trees on the ground, toward the lower left and as long as the
 * hour makes them (the figures' own, `sunAt`): drawn over the ground and under everything standing
 * on it. Nothing at night, indoors or with lighting off (the caller checks those last two).
 */
export function drawSceneryShadows(
  g: CanvasRenderingContext2D,
  props: readonly Prop[],
  ox: number,
  oy: number,
  W: number,
  H: number,
  hour: number,
): void {
  const sun = sunAt(hour);
  if (sun.cast <= 0.01) return;
  let casters = CASTERS.get(props);
  if (!casters) CASTERS.set(props, (casters = props.filter((p) => p.cast)));
  g.save();
  g.imageSmoothingEnabled = false;
  g.globalAlpha = sun.cast;
  for (const p of casters) {
    const c = p.cast!;
    const len = c.h * sun.len;
    const x = Math.round(c.x + ox);
    const y = Math.round(c.y + oy);
    // Off screen, shadow and all.
    const reach = len + (c.kind === 'block' ? c.w * 16 : c.half) + 8;
    if (x < -reach || x > W + reach || y < -reach || y > H + reach) continue;
    if (c.kind === 'block') {
      const s = blockSprite(c.w, Math.max(0.25, Math.round((len / TILE_Y) * 8) / 8), sun.tint);
      g.drawImage(s.img, x - s.ox, y - s.oy);
    } else {
      // The trunk's thin shadow, then the crown's, further out along it.
      drawCastShadow(g, x, y, len * 0.6, 1.5, sun.cast, 0, sun.tint);
      const at = len * 0.4;
      drawCastShadow(g, x + DX * at, y + DY * at, len * 0.6, c.half, sun.cast, 0, sun.tint);
    }
  }
  g.restore();
}

// ───────────────────────── night ─────────────────────────

const BAYER = [0, 8, 2, 10, 12, 4, 14, 6, 3, 11, 1, 9, 15, 7, 13, 5];
const bayer = (x: number, y: number): number => (BAYER[(y & 3) * 4 + (x & 3)]! + 0.5) / 16;

const POOLS = new Map<string, HTMLCanvasElement>();
/**
 * A pool of light `r` across and `r * squash` high each way from its middle, in hard bands with
 * ordered dither between them (no smooth gradient): `rgb` at `peak` alpha in the middle, falling off
 * in four steps to nothing at the rim. `inner`: the share of the radius kept at full strength (default half).
 */
export function pool(r: number, squash: number, rgb: string, peak: number, inner = 0.5): HTMLCanvasElement {
  const key = `${r}|${squash}|${rgb}|${peak}|${inner}`;
  const hit = POOLS.get(key);
  if (hit) return hit;
  const ry = Math.max(1, Math.round(r * squash));
  const c = canvas(r * 2, ry * 2);
  const g = ctx2d(c);
  const img = g.createImageData(r * 2, ry * 2);
  const [R, G, B] = rgb.split(',').map(Number) as [number, number, number];
  for (let y = 0; y < ry * 2; y++)
    for (let x = 0; x < r * 2; x++) {
      const d = Math.hypot((x + 0.5 - r) / r, (y + 0.5 - ry) / ry);
      if (d >= 1) continue;
      // Brightest over the inner part, then down in steps; the dither picks the step at each pixel.
      const a = Math.min(1, (1 - d) / (1 - inner));
      const q = Math.floor(a * 4 + bayer(x, y)) / 4;
      if (q <= 0) continue;
      const k = (y * r * 2 + x) * 4;
      img.data[k] = R;
      img.data[k + 1] = G;
      img.data[k + 2] = B;
      img.data[k + 3] = Math.round(q * peak * 255);
    }
  g.putImageData(img, 0, 0);
  POOLS.set(key, c);
  return c;
}

/** A light at night: where it is on screen, its size and whether it is a warm flame. */
type Light = { x: number; y: number; r: number; squash: number; warm: boolean; rings?: number; key?: number; inner?: number };

/**
 * A light that stands in the world (map tiles, height in art px over the ground): a lantern, a torch, a brazier, a
 * lit window or door. `r` is its pool's radius (px), `warm` its spill (0–1), `flicker` a flame's.
 */
export interface NightLight {
  readonly x: number;
  readonly y: number;
  readonly lift: number;
  readonly r: number;
  readonly squash?: number;
  readonly warm?: number;
  readonly flicker?: boolean;
}
/** Gives a map's standing lights (or none); asked once per map and kept. */
export type NightLightSource = (m: GameMap) => readonly NightLight[];
const SOURCES: NightLightSource[] = [];
const MAP_LIGHTS = new WeakMap<GameMap, readonly NightLight[]>();
/** Adds a source of standing night lights (e.g. the plains kit's lanterns: render/kit/lights.ts). */
export function registerNightLights(f: NightLightSource): void {
  SOURCES.push(f);
}
function mapLights(m: GameMap): readonly NightLight[] {
  let l = MAP_LIGHTS.get(m);
  if (!l) MAP_LIGHTS.set(m, (l = SOURCES.flatMap((f) => f(m))));
  return l;
}
/** Standing lights drawn at most per frame, by graphics tier (the nearest to the player kept). */
const LIGHT_CAP = [24, 40, 64, 96, 128] as const;

/** What the night needs from the renderer for a frame. */
export interface NightView {
  g: CanvasRenderingContext2D;
  /** A screen-sized layer composed on the GPU (render/webgl.ts). */
  gpu: {
    beginLayer(name: string, width: number, height: number): CanvasRenderingContext2D;
    endLayer(name: string): HTMLCanvasElement;
  };
  world: World;
  iso: IsoCamera;
  ox: number;
  oy: number;
  W: number;
  H: number;
  /** A figure's drawn height in pixels. */
  heightOf(e: Entity): number;
  /** How high the ground under a figure is drawn (a boulder, a roof). */
  groundOf(e: Entity): number;
  /** Not drawn: indoors, flown off. */
  hidden(e: Entity): boolean;
}

/**
 * The hour on the world: warm light at dusk and dawn, dark blue at night with pools of light round
 * the player (big enough to take in their whole figure), lanterns carried at the hand, the stone
 * lanterns of the plaza and the doors of houses and the inn.
 */
export function drawNight(v: NightView): void {
  const { dark, warm } = nightAt(v.world.hour);
  const { g, W, H } = v;
  if (warm > 0.01) {
    g.save();
    g.globalCompositeOperation = 'multiply';
    g.fillStyle = `rgba(255,${Math.round(205 - 25 * warm)},${Math.round(175 - 35 * warm)},${0.55 * warm})`;
    g.fillRect(0, 0, W, H);
    g.restore();
  }
  if (dark < 0.01) return;
  // Composed on the GPU: a screen-sized canvas redrawn and uploaded every frame cost more than
  // the rest of the night together.
  const n = v.gpu.beginLayer('night', W, H);
  n.fillStyle = `rgba(14,18,46,${0.6 * dark})`;
  n.fillRect(0, 0, W, H);
  const lights: Light[] = [];
  const at = (x: number, y: number, lift: number, r: number, squash: number, warmLight: boolean, inner?: number) => {
    const [sx, sy] = v.iso.proj(x, y);
    const px = Math.round(sx + v.ox);
    const py = Math.round(sy + v.oy - lift);
    const ry = r * squash;
    if (px > -r && px < W + r && py > -ry && py < H + ry)
      lights.push({ x: px, y: py, r, squash, warm: warmLight, inner });
  };
  const w = v.world;
  const p = w.player;
  // Round the player: centred a little over the waist with a wide full-strength middle (3/4 of the radius), so the
  // head is lit as much as the body and the feet (it sat in the dimmer outer steps before: DECISIONS 2026-10-13).
  const ph = v.heightOf(p);
  at(p.x, p.y, ph * 0.55 + p.z + v.groundOf(p), Math.round(ph * 0.8), 0.9, false, 0.75);
  for (const e of w.entities.values())
    if (e.folk?.lantern && !v.hidden(e)) {
      const h = v.heightOf(e);
      // Centred on the lantern's paper, just under where it hangs from.
      at(e.x, e.y, lanternLift(e, h) - 4 + v.groundOf(e), Math.round(h * 0.6), 0.7, true);
    }
  const P0 = v.iso.unturn(w.map.plaza.x, w.map.plaza.y);
  for (const [x, y] of plazaLanternRing(P0[0], P0[1])) at(x, y, 12, 26, 0.6, true);
  for (const st of w.map.structures)
    if (st.kind === 'house' || st.kind === 'inn' || st.kind === 'pagoda')
      at(st.i + st.w / 2, st.j + st.h + 0.3, 6, st.kind === 'inn' ? 44 : 32, 0.6, true);
  // Standing lights from the registered sources: flames flicker by a pixel step, the nearest kept under the tier's cap.
  const standing: Light[] = [];
  const t = w.time;
  for (const s of mapLights(w.map)) {
    const [sx, sy] = v.iso.proj(s.x, s.y);
    const px = Math.round(sx + v.ox);
    const py = Math.round(sy + v.oy - s.lift);
    const fl = s.flicker ? Math.round(Math.sin(t * (7 + hash2(s.x * 7, s.y) * 5) + s.x * 3.1) * 1.4) * 2 : 0;
    const r = Math.max(4, s.r + fl);
    const ry = r * (s.squash ?? 0.65);
    if (px > -r && px < W + r && py > -ry && py < H + ry)
      standing.push({ x: px, y: py, r, squash: s.squash ?? 0.65, warm: true, rings: s.warm ?? 1, key: Math.hypot(px - W / 2, py - H / 2) });
  }
  const cap = LIGHT_CAP[tierIndex()] ?? 64;
  if (standing.length > cap) standing.sort((a, b) => a.key! - b.key!);
  lights.push(...standing.slice(0, cap));
  n.globalCompositeOperation = 'destination-out';
  for (const l of lights) {
    const img = pool(l.r, l.squash, '0,0,0', 0.92, l.inner);
    n.drawImage(img, l.x - l.r, l.y - img.height / 2);
  }
  g.drawImage(v.gpu.endLayer('night'), 0, 0);
  // Lantern, window and door light spills warm round the flame.
  g.save();
  g.globalCompositeOperation = 'lighter';
  g.globalAlpha = dark;
  for (const l of lights)
    if (l.rings !== undefined) {
      // A standing light: three hard, dithered rings of warm spill (wide and faint, middle, a hot core).
      for (const [k, rgb, a] of [
        [0.95, '255,128,48', 0.1],
        [0.6, '255,150,64', 0.16],
        [0.28, '255,214,150', 0.34],
      ] as const) {
        const r = Math.max(2, Math.round(l.r * k));
        const img = pool(r, l.squash, rgb, a * l.rings);
        g.drawImage(img, l.x - r, l.y - img.height / 2);
      }
    } else if (l.warm) {
      const r = Math.round(l.r * 0.75);
      const img = pool(r, l.squash, '255,150,64', 0.16);
      g.drawImage(img, l.x - r, l.y - img.height / 2);
    }
  g.restore();
}
